import {
  getMiloDeeplink,
  getTransferEndpoints,
  updateContactReleaseToAIL,
  getContactEventLoggingAPIHandler,
} from "../api/session-api";
import {
  getAgentContactById,
  getActiveConnections,
} from "../amazon-connect/api/call";
import {
  API_CALL_ANSWER,
  API_CALL_TRANSFER_ENDPOINTS,
  INTERNAL_CALL_RELEASE,
  STATE_CALL,
  API_MILO_CHANNEL_TRANSACTION,
  ATTACH_CONTACT_TO_CALLBACK,
  STATE_CONFERENCE,
  STATE_MISSED_CALL,
} from "../constants/events";

import {
  isDialCallInProgress,
  resetConference,
  resetNormalCall,
  updateNormalCall,
  updateTransferEndpoints,
  updateConferenceTransferEndpoints,
  isDialConferenceInProgress,
  updateConference,
} from "../actions/call";
import { callPlayer, updateConferenceAndSend } from "../amazon-connect/handlers/call";
import { connectSession } from "../amazon-connect/handlers/session";
import storeRTK from "../store/storeRTK";
import Emitter from "../emitter";
import { RING_TYPES } from "../constants/constants";
import { historyInitialiseHandler } from "./history";
import { setSelectedTab } from "../actions/common";
import { notificationManager } from "../utils/NotificationManager";
import { SessionEvent } from "../types/call.type";
import {
  DIAL_CALL_IN_PROGRESS,
  DIAL_CONFERENCE_IN_PROGRESS,
} from "../constants/action-types";
import { CONTACT_EVENT_STATE_MAP } from "../constants/constants";
import { addNotificationAction } from "../connector/notifications";
import { createMissedCallNotification } from "../actions/notifications";
import { showErrorToast } from "../utils/utils";

// Track cleaned up conference IDs to prevent duplicate cleanup
const cleanedUpConferenceIds = new Set<string>();

// Function to clean up orphaned conference calls
function cleanupOrphanedConferenceCalls() {
  const state = storeRTK.getState();
  const currentConference = state.conference;

  // If no conference in state, nothing to clean up
  if (!currentConference?.id) {
    return;
  }

  // For held conferences, we still need to validate if the contact ID exists
  // If the contact ID no longer exists, it's an orphaned call that should be removed
  const isConferenceHeld =
    currentConference?.self?.held || currentConference?.customer?.held;

  if (isConferenceHeld) {
    console.log(
      `Conference ${currentConference.id} is held, checking for orphaned state`
    );

    // Check if contact ID still exists even when held
    const activeConnections = getActiveConnections();
    const conferencePhoneNumber =
      currentConference?.phoneNumber ||
      currentConference?.customer?.phoneNumber ||
      currentConference?.customer?.displayName;

    if (!conferencePhoneNumber) {
      console.log("Held conference has no phone number, treating as orphaned");
      cleanedUpConferenceIds.add(currentConference.id);
      updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: currentConference?.id,
        getContactId: () => currentConference?.id,
      });
      Emitter.emit(INTERNAL_CALL_RELEASE, currentConference);
      storeRTK.dispatch(resetConference());
      setTimeout(() => {
        cleanedUpConferenceIds.delete(currentConference.id);
      }, 10000);
      return;
    }

    const isActive = activeConnections.some(
      (conn) =>
        (conn.phoneNumber === conferencePhoneNumber ||
          conn.connection?.getEndpoint?.()?.phoneNumber ===
            conferencePhoneNumber) &&
        (conn.isConnected || conn.isOnHold) // Consider held connections as active
    );

    if (!isActive) {
      console.log(
        "Held conference contact ID no longer exists, cleaning up orphaned call"
      );
      cleanedUpConferenceIds.add(currentConference.id);
      updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: currentConference?.id,
        getContactId: () => currentConference?.id,
      });
      Emitter.emit(INTERNAL_CALL_RELEASE, currentConference);
      storeRTK.dispatch(resetConference());
      setTimeout(() => {
        cleanedUpConferenceIds.delete(currentConference.id);
      }, 10000);
    } else {
      console.log(
        `Held conference ${currentConference.id} is still active, keeping it`
      );
    }
    return;
  }

  // Prevent duplicate cleanup for the same conference
  if (cleanedUpConferenceIds.has(currentConference.id)) {
    console.log(
      `Conference ${currentConference.id} already cleaned up, skipping`
    );
    return;
  }

  console.log("Checking for orphaned conference calls...");

  try {
    // Get all active connections from Amazon Connect
    const activeConnections = getActiveConnections();
    console.log("Active connections from Amazon Connect:", activeConnections);

    // Check if the conference call is still active in Amazon Connect
    const conferencePhoneNumber =
      currentConference?.phoneNumber ||
      currentConference?.customer?.phoneNumber ||
      currentConference?.customer?.displayName;

    if (!conferencePhoneNumber) {
      console.log(
        "No conference phone number found, cleaning up conference state"
      );
      // Mark as cleaned up before cleanup to prevent duplicates
      cleanedUpConferenceIds.add(currentConference.id);
      updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: currentConference?.id,
        getContactId: () => currentConference?.id,
      });
      Emitter.emit(INTERNAL_CALL_RELEASE, currentConference);
      storeRTK.dispatch(resetConference());
      // Only emit minimal events for silent cleanup
      Emitter.emit(STATE_CONFERENCE, {
        body: { ...currentConference, self: { state: "ENDED" } },
      });
      return;
    }

    // Check if conference connection is still active (including held connections)
    const isConferenceActive = activeConnections.some(
      (conn) =>
        (conn.phoneNumber === conferencePhoneNumber ||
          conn.connection?.getEndpoint?.()?.phoneNumber ===
            conferencePhoneNumber) &&
        (conn.isConnected || conn.isOnHold) // Consider held connections as active
    );

    console.log(
      `Conference call ${conferencePhoneNumber} is ${
        isConferenceActive ? "active" : "inactive"
      } in Amazon Connect`
    );

    if (!isConferenceActive) {
      console.log(
        "Conference call is not active in Amazon Connect, cleaning up UI state"
      );
      // Mark as cleaned up before cleanup to prevent duplicates
      cleanedUpConferenceIds.add(currentConference.id);
      updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: currentConference?.id,
        getContactId: () => currentConference?.id,
      });
      Emitter.emit(INTERNAL_CALL_RELEASE, currentConference);
      storeRTK.dispatch(resetConference());

      // Only emit STATE_CONFERENCE for UI updates, avoid INTERNAL_CONFERENCE_RELEASE
      // to prevent duplicate toast notifications
      Emitter.emit(STATE_CONFERENCE, {
        body: { ...currentConference, self: { state: "ENDED" } },
      });

      // Update contact release for analytics
      updateContactReleaseToAIL(
        "voice",
        currentConference.id,
        conferencePhoneNumber
      );

      // Clean up the ID from the set after a delay to allow for legitimate future cleanups
      setTimeout(() => {
        cleanedUpConferenceIds.delete(currentConference.id);
      }, 10000); // 10 seconds
    }
  } catch (error) {
    console.error("Error during conference cleanup:", error);
    // On error, clean up the conference state to be safe
    cleanedUpConferenceIds.add(currentConference.id);
    updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
      contactId: currentConference?.id,
      getContactId: () => currentConference?.id,
    });
    Emitter.emit(INTERNAL_CALL_RELEASE, currentConference);
    storeRTK.dispatch(resetConference());
    Emitter.emit(STATE_CONFERENCE, {
      body: { ...currentConference, self: { state: "ENDED" } },
    });

    // Clean up the ID from the set after a delay
    setTimeout(() => {
      cleanedUpConferenceIds.delete(currentConference.id);
    }, 10000);
  }
}

export async function callHandler(event: any) {
  const callPresent = storeRTK.getState().call.id;
  if (
    event?.self?.state !== "RINGING" &&
    event?.self?.direction != "OUTBOUND"
  ) {
    if (callPresent) {
      //audioManager.stop(RING_TYPES.CALL);
      notificationManager.stopFaviconBlinking();
    }
  }
  if (event?.self?.state) {
    if (
      event.self?.state == "RELEASED" ||
      event.self?.state == "DISCONNECTED"
    ) {
      const cstate = storeRTK.getState();
      if (cstate.call.id === event.id) {
        Emitter.emit(INTERNAL_CALL_RELEASE, event);
        storeRTK.dispatch(resetNormalCall());
        Emitter.emit(STATE_CALL, { body: event });
        updateContactReleaseToAIL(
          "voice",
          event.id as string,
          event.customer?.phoneNumber || ""
        );

        // Clean up any orphaned conference calls when main call ends
        setTimeout(() => {
          cleanupOrphanedConferenceCalls();
        }, 2000); // Give Amazon Connect time to send events
      } else {
        //if call disconnected from CCP but visible in UI
        historyInitialiseHandler();
        storeRTK.dispatch(resetNormalCall());
        Emitter.emit(STATE_CALL, { body: event });
      }
    } else if (event) {
      const callNotPresent = !storeRTK.getState().call.id;
      if (event.self?.state == "DIALING") {
        getContactEventLoggingAPIHandler(event?.id ?? "", "DIALING");
      }
      storeRTK.dispatch(updateNormalCall(event));

      if (callNotPresent) {
        const state = storeRTK.getState();
        const { self } = state.call;
        const callId = state.call?.id;
        if (self?.direction != "OUTBOUND") {
          getContactEventLoggingAPIHandler(event?.body?.id ?? "", "RINGING");
          notificationManager.startFaviconBlinking();
          storeRTK.dispatch(setSelectedTab("Call"));
          notificationManager.show("You have an incoming call", {
            body: "Click or open iHub to answer it",
            icon: "icon.png",
            onclick: () => {
              Emitter.emit(API_CALL_ANSWER, {
                body: { conversationId: callId },
              });
            },
          });
        }
      }
    }
  }
}

export async function conferenceHandler(event: any) {
  const conferencePresent = storeRTK.getState().conference.id;
  if (
    event?.self?.state !== "RINGING" &&
    event?.self?.direction != "OUTBOUND"
  ) {
    if (conferencePresent) {
      //audioManager.stop(RING_TYPES.CALL);
      notificationManager.stopFaviconBlinking();
    }
  }
  if (event?.self?.state) {
    if (
      event.self?.state == "RELEASED" ||
      event.self?.state == "DISCONNECTED"
    ) {
      const cstate = storeRTK.getState();
      if (cstate.conference.id === event.id) {
        Emitter.emit(INTERNAL_CALL_RELEASE, event);
        storeRTK.dispatch(resetConference());
        Emitter.emit(STATE_CONFERENCE, { body: event });
        updateContactReleaseToAIL(
          "voice",
          event.id as string,
          event?.phoneNumber || ""
        );
      } else {
        historyInitialiseHandler();
        storeRTK.dispatch(resetConference());
        Emitter.emit(STATE_CONFERENCE, { body: event });
      }
      // IMPORTANT: Return here to prevent further processing of this event
      // This prevents the conference state from being repopulated immediately after reset
      return;
    } else if (event.self?.state == "TERMINATED") {
      //Do nothing - but still return to prevent further processing
      return;
    } else if (event) {
      if (event.self?.state == "DIALING") {
        getContactEventLoggingAPIHandler(event?.id ?? "", "DIALING");
        Emitter.emit(ATTACH_CONTACT_TO_CALLBACK);
      }
      storeRTK.dispatch(updateConference(event));
      // Only update conference state if it's not a terminal state
      if (
        event.self?.state !== "RELEASED" &&
        event.self?.state !== "DISCONNECTED"
      ) {
        // Prevent re-entrancy loop: if session conference already matches event, skip refresh
        try {
          const sessionConf = connectSession.getConferenceCall() || {};
          const isSame =
            JSON.stringify(sessionConf) === JSON.stringify(event || {});
          if (!isSame) {
            const contact = getAgentContactById(true);
            if (contact) {
              updateConferenceAndSend("REFRESH", contact, {
                muted: event.self?.muted || false,
              });
            }
          }
        } catch {}
      }
    }
  }
}

async function miloHandlerEvent(ev: SessionEvent<any>) {
  const miloEvent = {
    body: {
      phoneNumber: ev.body.phoneNumber,
      contactId: ev.body.contactId,
    },
  };
  notificationManager.triggerMilo(miloEvent, "call");
}

export async function miloHandler(ev: any) {
  const miloDeeplink = await getMiloDeeplink(
    "voice",
    ev.body?.contactId,
    ev.body?.phoneNumber
  );
  if (miloDeeplink) {
    // put timeout because milo can come immediately and bg state may not be updated till then.
    setTimeout(() => {
      window.open(miloDeeplink, "_blank");
    }, 100);
  }
}

export async function setTransferEndpoints(contactId: string | undefined) {
  if (contactId) {
    const allowedTransferEndpoints = await getTransferEndpoints(contactId);
    storeRTK.dispatch(updateTransferEndpoints(allowedTransferEndpoints));

    // Emit event so CallTransferDialog component can update its state
    Emitter.emit(API_CALL_TRANSFER_ENDPOINTS, {
      body: {
        transferEndpoints: allowedTransferEndpoints,
        conversationId: contactId,
      },
    });
  } else {
    console.log("Error: error in setting transfer endpoints");
  }
}

export async function setConferenceTransferEndpoints(
  contactId: string | undefined
) {
  if (contactId) {
    const allowedTransferEndpoints = await getTransferEndpoints(contactId);
    storeRTK.dispatch(
      updateConferenceTransferEndpoints(allowedTransferEndpoints)
    );

    // Emit event so transfer dialog components can update their state
    Emitter.emit(API_CALL_TRANSFER_ENDPOINTS, {
      body: {
        transferEndpoints: allowedTransferEndpoints,
        conversationId: contactId,
      },
    });
  } else {
    console.log("Error: error in setting conference transfer endpoints");
  }
}

const handleCalldialProgress = () => {
  storeRTK.dispatch(isDialCallInProgress(false));
};

const handleConferencedialProgress = () => {
  storeRTK.dispatch(isDialConferenceInProgress(false));
};

const missedCallHandlerEvent = (ev: SessionEvent<any>) => {
  console.log("Missed call event in controller: ", ev);
  
  const eventData = ev.body;
  
  // Create missed call notification
  const notification = createMissedCallNotification(
    eventData.contactId,
    eventData.customerName,
    eventData.phoneNumber,
    eventData.queueName
  );
  
  console.log("Created notification: ", notification);
  
  // Dispatch the notification to the Redux store
  const action = addNotificationAction(notification);
  
  try {
    storeRTK.dispatch(action);
    console.log("Action dispatched successfully");
  } catch (error) {
    console.error("Error dispatching action: ", error);
  }
  
  // Also display a persistent toast notification
  showErrorToast("You have a missed call from " + eventData.phoneNumber + " in queue " + eventData.queueName + ".", "missedCallNotification", 1*60*60*1000); // 1 hour
};

// async function loggingHandlerEvent(ev: SessionEvent<any>) {
//   const loggingEvent = {
//     body: {
//       contactId: ev.body?.contactId,
//       eventType: ev.body?.eventType,
//     },
//   };
//   notificationManager.eventLoggingAPI(loggingEvent, "call");
// }

// export async function contactEventHandler(ev: SessionEvent<any>) {
//   getContactEventLoggingAPIHandler(ev.body?.contactId, ev.body?.eventType);
// }

// Test function to manually add a notification
export function testNotification() {
  console.log("Testing notification system...");
  
  const testNotification = createMissedCallNotification(
    "test-contact-123",
    "Test Customer",
    "+1234567890",
    "Test Queue"
  );
  
  console.log("Test notification: ", testNotification);
  
  const action = addNotificationAction(testNotification);
  console.log("Test action: ", action);
  
  try {
    storeRTK.dispatch(action);
    console.log("Test notification dispatched successfully");
    
    // Check state immediately
    const state = storeRTK.getState();
    console.log("Test - Current notifications state: ", state.notifications);
  } catch (error) {
    console.error("Error dispatching test notification: ", error);
  }
}

// Expose test function globally for debugging
declare global {
  interface Window {
    testNotification: typeof testNotification;
  }
}

window.testNotification = testNotification;

export default function registerCallHandlers() {
  Emitter.on(STATE_CALL, callHandler);
  Emitter.on(STATE_CONFERENCE, conferenceHandler);
  Emitter.on(API_MILO_CHANNEL_TRANSACTION, miloHandlerEvent);
  Emitter.on(DIAL_CALL_IN_PROGRESS, handleCalldialProgress);
  Emitter.on(DIAL_CONFERENCE_IN_PROGRESS, handleConferencedialProgress);
  Emitter.on(STATE_MISSED_CALL, missedCallHandlerEvent); // For missed call notification
}
